import React, { useEffect, useMemo, useState } from 'react';
import { X, MagnifyingGlass } from '@phosphor-icons/react';
import { eventAPI } from '../../lib/eventApi';

// Small picker over eligible profiles. Returns the picked profile to the parent
// via onPick({ profile_id, name }). Excludes the current registered
// player (excludeProfileId).
//
// Uses the session-scoped `/event-sessions/{id}/available-players` endpoint
// so assigned Event Managers (not just admins) can pick subs. Response is
// trimmed to safe public fields (no email/phone).
export default function SubPickerDialog({ open, sessionId, onClose, onPick, excludeProfileId }) {
  const [profiles, setProfiles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    eventAPI.listAvailablePlayers(sessionId)
      .then((r) => setProfiles(r.data?.profiles || []))
      .catch(() => setProfiles([]))
      .finally(() => setLoading(false));
  }, [open, sessionId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles
      .filter((p) => p.profile_id !== excludeProfileId)
      .filter((p) => {
        if (!q) return true;
        return (p.name || '').toLowerCase().includes(q);
      })
      .slice(0, 30);
  }, [profiles, search, excludeProfileId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="sub-picker-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3"
        data-testid="sub-picker-dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl text-[#013523]">Pick substitute</h3>
            <p className="text-xs text-[#6a766e]">Points still credit the registered player.</p>
          </div>
          <button onClick={onClose} className="text-[#6a766e]" data-testid="sub-picker-close">
            <X size={20} />
          </button>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
          <input
            data-testid="sub-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            placeholder="Search by name…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
          />
        </div>
        <ul className="max-h-72 overflow-y-auto divide-y divide-[#e3dcd1] border border-[#e3dcd1] rounded-lg" data-testid="sub-candidate-list">
          {loading && <li className="px-3 py-2 text-xs text-[#6a766e]">Loading…</li>}
          {!loading && filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-[#6a766e]">No matches</li>
          )}
          {filtered.map((p) => (
            <li
              key={p.profile_id}
              className="px-3 py-2 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40 cursor-pointer"
              data-testid={`sub-candidate-${p.profile_id}`}
              onClick={() => { onPick?.(p); onClose?.(); }}
            >
              <div className="min-w-0">
                <div className="text-sm text-[#013523] truncate">{p.name}</div>
              </div>
              <button className="text-xs px-2 py-1 rounded-md border border-[#015e3f] text-[#015e3f]">
                Pick
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
